import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import useAppContext from 'context/AppContextProvider';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { requestData } from 'services/APIClient';
import { UBER_EATS_CALLBACK_URL } from 'services/APIConstants';
import Interceptor from 'services/Interceptor';
import AuthToken, { isLoggedIn } from 'services/Token';

const UberEatsCallbackHandler = () => {
  const { pos } = useAppContext();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setMessage(`Uber Eats authorization failed: ${error}`);
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage('No authorization code received from Uber Eats');
        return;
      }

      if (!pos.current) {
        setStatus('error');
        setMessage('Store ID not found');
        return;
      }

      try {
        const response = await requestData(
          `${UBER_EATS_CALLBACK_URL(pos.current)}?code=${code}`,
          AuthToken(),
          isLoggedIn() ? Interceptor : null
        );

        // Handle successful response
        if (response.success) {
          setStatus('success');
          setMessage(
            response.message ||
              'Uber Eats authorization completed successfully!'
          );
          setData(response.data);
        } else {
          // Handle error response - check both message and error fields
          setStatus('error');
          setMessage(
            response.message ||
              response.error ||
              'Failed to complete Uber Eats authorization'
          );
        }
      } catch (error) {
        setStatus('error');
        // Extract error message from various possible locations
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'An error occurred during authorization';
        setMessage(errorMessage);
      }
    };

    handleCallback();
  }, [searchParams, pos]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      p={3}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%' }}>
        {status === 'processing' && (
          <>
            <CircularProgress
              size={60}
              sx={{ mb: 3, display: 'block', mx: 'auto' }}
            />
            <Typography variant="h5" gutterBottom align="center">
              Processing Uber Eats Authorization...
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center">
              Please wait while we complete the setup.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6">Success!</Typography>
              <Typography>{message}</Typography>
            </Alert>
            {data && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Store(s) Found:</strong> {data.store_count}
                </Typography>
                {data.stores &&
                  data.stores.map((store, index) => (
                    <Paper
                      key={store.id || index}
                      variant="outlined"
                      sx={{ p: 2, mt: 1 }}
                    >
                      <Typography variant="subtitle2">
                        {store.name || data.uber_store_name || 'Unknown Store'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {store.location?.street_address_line_one ||
                          store.address?.street_address_line_one ||
                          'Address not available'}
                      </Typography>
                      {data.uber_store_id && (
                        <Typography variant="body2" color="text.secondary">
                          Store ID: {data.uber_store_id}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                {data.bevz_store_id && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Bevz Store ID:</strong> {data.bevz_store_id}
                  </Typography>
                )}
              </Box>
            )}
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => navigate(`/stores/${pos.current}`)}
            >
              Back to Store page
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6">Authorization Failed</Typography>
              <Typography>{message}</Typography>
            </Alert>
            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => navigate(`/stores/${pos.current}`)}
            >
              Back to Store page
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default UberEatsCallbackHandler;
